import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, throwError } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';

interface PanelConfigurationPermission {
  id: number;
  user_type: number;
  user_type_name: string;
  transfer: boolean;
  approve: boolean;
  dispose: boolean;
  sale: boolean;
  srNo?: number;
}

/**
 * The Panel action matrix: one row per user type, one checkbox per action.
 * Listing and updating only — rows are seeded one-per-UserType by migration
 * 0052, and the Customer type is excluded server-side. Mirrors
 * FsrConfigurationComponent exactly.
 */
@Component({
  selector: 'app-panel-configuration',
  templateUrl: './panel-configuration.component.html',
  styleUrls: ['./panel-configuration.component.scss'],
})
export class PanelConfigurationComponent implements OnInit {
  @Input() permissionsOverride?: { canAdd: boolean; canEdit: boolean; canView: boolean; canDelete: boolean };

  public userTypePermission: PanelConfigurationPermission[] = [];

  readonly permissionColumns: { key: keyof PanelConfigurationPermission; label: string }[] = [
    { key: 'transfer', label: 'Transfer' },
    { key: 'approve', label: 'Approve' },
    { key: 'dispose', label: 'Dispose' },
    { key: 'sale', label: 'Sale' },
  ];

  searchElement = '';
  searchSubject: Subject<string> = new Subject();
  loader = false;
  tableLoader = false;

  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady = false;

  constructor(
    private _utilityService: UtilityService,
    private toaster: ToastrService,
    private permService: PermissionsService
  ) {}

  ngOnInit(): void {
    this.initComponent();
  }

  async initComponent() {
    this.loader = true;
    try {
      await this.getComponentPermissions();
      this.searchSubject.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
        this.searchElement = searchTerm;
        this.getTableData();
      });
      if (this.permissions.canView) {
        this.getTableData();
      }
    } finally {
      this.loader = false;
    }
  }

  async getComponentPermissions() {
    if (this.permissionsOverride) {
      this.permissions = { ...this.permissionsOverride };
      this.permissionsReady = true;
      return;
    }

    const url = this.permService.getCurrentUrl();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();
    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
          canAdd: !!matchedMenu.canAdd,
          canEdit: !!matchedMenu.canEdit,
          canView: !!matchedMenu.canView,
          canDelete: !!matchedMenu.canDelete,
        }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }

  private getTableData(): void {
    this.tableLoader = true;
    this.userTypePermission = [];
    this._utilityService.getPanelConfigurationList(this.searchElement).subscribe({
      next: (res: any) => {
        if (Array.isArray(res)) {
          this.userTypePermission = res.map((item: any, index: number) => ({
            ...item,
            srNo: index + 1,
          }));
        }
        this.tableLoader = false;
      },
      error: () => {
        this.tableLoader = false;
        this.toaster.error('Failed to load Panel configuration');
      },
    });
  }

  onPermissionChange(item: PanelConfigurationPermission, field: string): void {
    const previousValue = !item[field];

    this._utilityService
      .updatePanelConfiguration({ id: item.id, [field]: item[field] })
      .pipe(
        catchError((error) => {
          item[field] = previousValue;
          this.handleApiFieldErrors(error, this.toaster);
          return throwError(() => error);
        })
      )
      .subscribe((response: any) => {
        if (response) {
          this.toaster.success('Permission updated successfully');
        }
      });
  }

  public searchData(event: Event): void {
    this.searchSubject.next((event.target as HTMLInputElement)?.value || '');
  }

  handleApiFieldErrors(error: HttpErrorResponse, toastr: ToastrService): void {
    if (error.error?.message) {
      toastr.error(error.error.message);
      return;
    }
    if (error.error?.errors) {
      Object.keys(error.error.errors).forEach((key) => {
        const message = Array.isArray(error.error.errors[key])
          ? error.error.errors[key][0]
          : error.error.errors[key];
        toastr.error(message);
      });
      return;
    }
    toastr.error('Sorry, something went wrong. Please try again.');
  }
}
